import React, { useState } from 'react';

import { Button } from '@/design-system/button';
import {
  DispatchDetailBatch,
  DispatchDetailStop,
} from '@/services/api/dispatches';

interface DeferredSectionProps {
  /** Whole DEFERRED batches (raw — carries stops for assignment). */
  batches: DispatchDetailBatch[];
  /** PREVIEW dispatches can act on held goods; committed ones are read-only. */
  editable: boolean;
  /** BE defer-reason code → ops-readable copy. */
  deferReasonLabel: (code: string | null) => string | null;
  /** Open the assign modal for a set of stops (one, or all of a batch). */
  onRequestAssign: (
    batch: DispatchDetailBatch,
    stops: DispatchDetailStop[]
  ) => void;
  /** Id currently mid-request (batch) — disables its button. */
  busy?: string | null;
}

/**
 * "Ditahan" — everything held back from the next commit, surfaced as a
 * dedicated bucket in the rider sidebar. Defer is stop-scoped: a deferred stop
 * (with all its items) lives in the dispatch's single rider-less DEFERRED
 * batch — there's no per-item defer flag anymore.
 *
 * On a PREVIEW dispatch ops restores held goods by assigning them onto a
 * rider — one stop at a time or the whole batch at once (opens the assign
 * modal, same shape as batch transfer). A committed dispatch shows everything
 * locked.
 */
const DeferredSection: React.FC<DeferredSectionProps> = ({
  batches,
  editable,
  deferReasonLabel,
  onRequestAssign,
  busy,
}) => {
  const [open, setOpen] = useState(true);

  if (batches.length === 0) return null;

  const heldItemTotal = batches.reduce((n, b) => n + b.itemCount, 0);
  const summary = [
    batches.length > 0 ? `${batches.length} batch` : null,
    `${heldItemTotal} item`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="overflow-hidden rounded-lg border border-orange-200 bg-orange-50">
      {/* Header — click to collapse. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className="flex cursor-pointer items-center gap-2.5 px-3 py-2.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-orange-400"
      >
        <span className="grid h-7 w-7 flex-none place-items-center rounded-lg bg-orange-100 text-orange-600">
          <i className="ri-pause-circle-line text-[16px]" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold tracking-tight text-orange-900">
            Deferred (Ditahan) — tidak ikut terkirim
          </p>
          <p className="text-[11px] text-orange-700 tabular-nums">{summary}</p>
        </div>
        <i
          className={`${
            open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
          } text-[16px] text-orange-700`}
          aria-hidden="true"
        />
      </div>

      {open && (
        <div className="flex flex-col gap-2.5 border-t border-orange-200 p-3">
          {/* Whole held batches — assignable per-stop or all at once. */}
          {batches.map((b) => {
            const batchBusy = busy === b.id;
            return (
              <div
                key={b.id}
                className="rounded-lg border border-orange-200 border-l-[3px] border-l-orange-500 bg-white"
              >
                {/* batch header */}
                <div className="border-b border-stroke-soft-200 px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-[12px] font-bold tracking-tight text-text-strong-950">
                      Batch belum kebagian rider
                    </p>
                    <span className="flex-none text-[11px] font-semibold text-text-sub-600 tabular-nums">
                      {b.itemCount} item · {b.stops.length} stop
                    </span>
                  </div>
                  {b.deferReason && (
                    <p className="mt-1 text-[11px] leading-snug text-orange-700">
                      {deferReasonLabel(b.deferReason)}
                    </p>
                  )}
                  {editable && (
                    <div className="mt-2">
                      <Button
                        tone="primary" style="stroke"
                        size="xs"
                        disabled={batchBusy}
                        onClick={() => onRequestAssign(b, b.stops)}
                      >
                        <i
                          className="ri-arrow-left-right-line mr-1 text-[12px]"
                          aria-hidden="true"
                        />
                        {batchBusy ? 'Meng-assign…' : 'Assign semua ke rider'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* per-stop / per-waybill rows */}
                <div className="divide-y divide-stroke-soft-200">
                  {b.stops.map((s, i) => {
                    const waybills = Array.from(
                      new Set(s.items.map((it) => it.waybill))
                    );
                    return (
                      <div key={s.id} className="flex items-start gap-2 px-3 py-2">
                        {/* Stop number — matches the numbered ghost marker on
                            the map (array order). */}
                        <span
                          className="mt-px grid h-5 w-5 flex-none place-items-center rounded-full border-2 border-stone-500 bg-white text-[10px] font-bold text-stone-500 tabular-nums"
                          aria-hidden="true"
                        >
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap gap-1">
                            {waybills.map((wb) => (
                              <span
                                key={wb}
                                className="rounded-md border border-orange-200 bg-orange-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-orange-800"
                              >
                                {wb}
                              </span>
                            ))}
                          </div>
                          <p className="mt-1 flex items-start gap-1 text-[10px] leading-snug text-text-sub-600">
                            <i
                              className={`${
                                s.kind === 'PICKUP'
                                  ? 'ri-inbox-unarchive-line'
                                  : 'ri-map-pin-line'
                              } mt-px flex-none text-[11px] text-text-soft-400`}
                              aria-hidden="true"
                            />
                            <span className="min-w-0">
                              {s.kind === 'PICKUP' && (
                                <span className="font-semibold text-verified-base">
                                  Pickup{' · '}
                                </span>
                              )}
                              {s.receiverName ? (
                                <span className="font-semibold text-text-sub-600">
                                  {s.receiverName}
                                  {' · '}
                                </span>
                              ) : null}
                              {s.destinationAddress || '—'}
                            </span>
                          </p>
                        </div>
                        {editable && (
                          <Button
                            tone="primary" style="stroke"
                            size="xs"
                            disabled={batchBusy}
                            onClick={() => onRequestAssign(b, [s])}
                          >
                            <i
                              className="ri-arrow-left-right-line mr-1 text-[12px]"
                              aria-hidden="true"
                            />
                            Assign
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="border-t border-stroke-soft-200 px-3 py-1.5 text-[11px] text-text-sub-600">
                  Barang tetap di hub (HUB_SCANNED).
                </p>
              </div>
            );
          })}

          {/* Editability-aware guidance. */}
          <p className="rounded-lg border border-orange-200 bg-orange-100/40 px-2.5 py-2 text-[11px] leading-relaxed text-orange-700">
            {editable ? (
              <>
                Klik <span className="font-semibold text-orange-900">Assign</span>{' '}
                per waybill atau{' '}
                <span className="font-semibold text-orange-900">
                  Assign semua ke rider
                </span>
                . Kalau tak ada rider yang muat, tambah rider dulu lewat{' '}
                <span className="font-semibold text-orange-900">Add riders</span>.
              </>
            ) : (
              <>
                Dispatch sudah di-commit — barang ini{' '}
                <span className="font-semibold text-orange-900">
                  tidak bisa diaktifkan lagi di sini
                </span>
                . Tetap di hub dan bisa direncanakan pada dispatch berikutnya.
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default DeferredSection;
